/**
 * NSGA2Page — multi-objective Pareto optimisation (simplified NSGA-II)
 *
 * Reed et al. 2003. Two-objective formulation:
 *   f1 = minimise lifetime cost (£M)
 *   f2 = minimise hypolimnion anoxia days/year
 *
 * Simplified NSGA-II: 30-member population, 30 generations, non-dominated
 * sorting + crowding distance to maintain diversity along the Pareto front.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { GitBranch, ArrowLeft, Play, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec,
  moduleAOTRkgh, blowerKwForModule,
} from '@/lib/diffuserPhysics';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

const RESERVOIR_AREA_M2 = 34_000;
const SOD_kgM2D = 0.6;
const sodTotal_kgH = SOD_kgM2D * RESERVOIR_AREA_M2 / 24;

interface ModSpec extends LegacyModSpec { waterDepthM: number; mountingHeightM: number; waterTempC: number; currentDoMgL?: number; alphaFactor?: number; betaFactor?: number; }
const MOD_DEFAULTS: ModSpec = { diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300, poreSizeUm: 80, airflowPerDiffNm3h: 1.2, waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12, currentDoMgL: 2.0, alphaFactor: 0.85, betaFactor: 0.98 };

interface DecVars { modules: number; airflow: number; mountingHeight: number; }
interface Sol { v: DecVars; f1: number; f2: number; rank?: number; }

function evaluate(spec: ModSpec, v: DecVars): { f1: number; f2: number } {
  const cSpec = { ...spec, airflowPerDiffNm3h: v.airflow, mountingHeightM: v.mountingHeight };
  const dSpec = specFromLegacy(cSpec);
  const H_eff = Math.max(0.3, cSpec.waterDepthM - cSpec.mountingHeight);
  const AOTR_mod = moduleAOTRkgh(dSpec, H_eff, cSpec.waterTempC, 0.85, 0.98, cSpec.currentDoMgL ?? 2.0);
  const kw_mod = blowerKwForModule(dSpec, H_eff);
  const AOTR_total = AOTR_mod * v.modules;
  // f1 — lifetime cost £M
  const capex = (1800 * v.modules + 25000 + 1200 * v.modules * kw_mod) / 1e6;
  const hours = 8760 * 0.55;
  const annual_opex = (kw_mod * v.modules * hours * 0.18 + 50 * v.modules) / 1e6;
  const r = 0.04;
  const opex_npv = annual_opex * (1 - Math.pow(1 + r, -20)) / r;
  const f1 = capex + opex_npv;
  // f2 — hypolimnion anoxia days/year (Cornett & Rigler scaled)
  const ratio = AOTR_total / sodTotal_kgH;
  const anoxia_days = Math.max(0, 120 - 100 * Math.min(1.5, ratio));
  return { f1, f2: anoxia_days };
}

function nonDominatedSort(pop: Sol[]): Sol[][] {
  const fronts: Sol[][] = [[]];
  const dominates = (a: Sol, b: Sol) => (a.f1 <= b.f1 && a.f2 <= b.f2) && (a.f1 < b.f1 || a.f2 < b.f2);
  const n: number[] = new Array(pop.length).fill(0);
  const S: number[][] = pop.map(() => []);
  pop.forEach((p, i) => {
    pop.forEach((q, j) => {
      if (i === j) return;
      if (dominates(p, q)) S[i].push(j);
      else if (dominates(q, p)) n[i]++;
    });
    if (n[i] === 0) { p.rank = 0; fronts[0].push(p); }
  });
  let f = 0;
  while (fronts[f] && fronts[f].length) {
    const next: Sol[] = [];
    fronts[f].forEach(p => {
      const idx = pop.indexOf(p);
      S[idx].forEach(qi => {
        n[qi]--;
        if (n[qi] === 0) { pop[qi].rank = f + 1; next.push(pop[qi]); }
      });
    });
    f++;
    if (next.length) fronts.push(next);
  }
  return fronts;
}

function tournamentSelect(pop: Sol[]): Sol {
  const a = pop[Math.floor(Math.random() * pop.length)];
  const b = pop[Math.floor(Math.random() * pop.length)];
  return (a.rank ?? 0) <= (b.rank ?? 0) ? a : b;
}

function crossover(a: DecVars, b: DecVars): DecVars {
  return {
    modules: Math.round((a.modules + b.modules) / 2),
    airflow: (a.airflow + b.airflow) / 2,
    mountingHeight: (a.mountingHeight + b.mountingHeight) / 2,
  };
}

function mutate(v: DecVars): DecVars {
  return {
    modules: Math.max(4, Math.min(40, v.modules + Math.round((Math.random() - 0.5) * 4))),
    airflow: Math.max(0.4, Math.min(2.5, v.airflow + (Math.random() - 0.5) * 0.3)),
    mountingHeight: Math.max(0.3, Math.min(1.2, v.mountingHeight + (Math.random() - 0.5) * 0.15)),
  };
}

export default function NSGA2Page() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('nsga2');
  const [spec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }));
  const [popSize, setPopSize] = useState(40);
  const [generations, setGens] = useState(40);
  const [pareto, setPareto] = useState<Sol[]>([]);
  const [allFinal, setAllFinal] = useState<Sol[]>([]);
  const [running, setRunning] = useState(false);

  const run = () => {
    setRunning(true);
    // initial population
    let pop: Sol[] = Array.from({ length: popSize }, () => {
      const v: DecVars = {
        modules: 4 + Math.floor(Math.random() * 36),
        airflow: 0.4 + Math.random() * 2.0,
        mountingHeight: 0.3 + Math.random() * 0.9,
      };
      const f = evaluate(spec, v);
      return { v, f1: f.f1, f2: f.f2 };
    });

    for (let g = 0; g < generations; g++) {
      // generate offspring
      const off: Sol[] = [];
      for (let i = 0; i < popSize; i++) {
        const child = mutate(crossover(tournamentSelect(pop).v, tournamentSelect(pop).v));
        const f = evaluate(spec, child);
        off.push({ v: child, f1: f.f1, f2: f.f2 });
      }
      // combine and select
      const combined = [...pop, ...off];
      const fronts = nonDominatedSort(combined);
      pop = [];
      let fi = 0;
      while (pop.length + (fronts[fi]?.length ?? 0) <= popSize && fi < fronts.length) {
        pop.push(...fronts[fi]);
        fi++;
      }
      if (pop.length < popSize && fronts[fi]) {
        pop.push(...fronts[fi].slice(0, popSize - pop.length));
      }
    }

    setAllFinal(pop);
    setPareto(pop.filter(s => s.rank === 0).sort((a, b) => a.f1 - b.f1));
    setRunning(false);
  };

  // Render scatter Pareto plot
  const svgW = 720, svgH = 320;
  const padL = 60, padR = 20, padT = 25, padB = 35;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const xMax = useMemo(() => allFinal.length ? Math.max(...allFinal.map(s => s.f1)) : 1, [allFinal]);
  const xMin = useMemo(() => allFinal.length ? Math.min(...allFinal.map(s => s.f1)) : 0, [allFinal]);
  const yMax = useMemo(() => allFinal.length ? Math.max(...allFinal.map(s => s.f2)) : 100, [allFinal]);
  const yMin = useMemo(() => allFinal.length ? Math.min(...allFinal.map(s => s.f2)) : 0, [allFinal]);
  const xp = (v: number) => padL + ((v - xMin) / Math.max(0.001, xMax - xMin)) * plotW;
  const yp = (v: number) => padT + plotH - ((v - yMin) / Math.max(0.001, yMax - yMin)) * plotH;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={GitBranch}
        title="Module — NSGA-II Multi-Objective Pareto"
        subtitle="Reed et al. 2003. Optimise cost vs anoxia days/year simultaneously"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${
              enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'
            }`}>
              <input type="checkbox" checked={enabled} onChange={() => toggle('nsga2')} className="accent-emerald-500" />
              Module {enabled ? 'ENABLED' : 'DISABLED'}
            </label>
            <button onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Modules
            </button>
          </div>
        }
      />

      <ModuleConflictBanner moduleId="nsga2" />

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm items-end">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Population size</label>
          <input type="number" min={10} max={100} value={popSize}
            onChange={e => setPopSize(Number(e.target.value))}
            className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Generations</label>
          <input type="number" min={10} max={100} value={generations}
            onChange={e => setGens(Number(e.target.value))}
            className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
        </div>
        <button onClick={run} disabled={running}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-500 disabled:opacity-50">
          <Play className="w-4 h-4" /> {running ? 'Running…' : 'Run NSGA-II'}
        </button>
      </div>

      {allFinal.length > 0 && (
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-2">Pareto front — cost vs anoxia trade-off</div>
          <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
            <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
            <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
            <text x={padL + plotW / 2} y={padT + plotH + 25} textAnchor="middle" fontSize={10} fill="#94a3b8">
              Lifetime cost £M (f₁)
            </text>
            <text x={20} y={padT + plotH / 2} textAnchor="middle" fontSize={10} fill="#94a3b8" transform={`rotate(-90, 20, ${padT + plotH / 2})`}>
              Anoxia days/yr (f₂)
            </text>
            {[0, 0.25, 0.5, 0.75, 1].map(f => {
              const xv = xMin + f * (xMax - xMin);
              const yv = yMin + f * (yMax - yMin);
              return (
                <g key={f}>
                  <text x={xp(xv)} y={padT + plotH + 15} textAnchor="middle" fontSize={9} fill="#94a3b8">{xv.toFixed(2)}</text>
                  <text x={padL - 4} y={yp(yv) + 3} textAnchor="end" fontSize={9} fill="#94a3b8">{yv.toFixed(0)}</text>
                </g>
              );
            })}
            {/* Dominated solutions (light) */}
            {allFinal.filter(s => s.rank !== 0).map((s, i) => (
              <circle key={`d${i}`} cx={xp(s.f1)} cy={yp(s.f2)} r={2.5} fill="#475569" opacity={0.5} />
            ))}
            {/* Pareto front (highlighted) */}
            {pareto.map((s, i) => (
              <g key={`p${i}`}>
                <circle cx={xp(s.f1)} cy={yp(s.f2)} r={4} fill="#22c55e" stroke="#0a1628" strokeWidth={0.7} />
                {i > 0 && (
                  <line x1={xp(pareto[i - 1].f1)} y1={yp(pareto[i - 1].f2)} x2={xp(s.f1)} y2={yp(s.f2)}
                    stroke="#22c55e" strokeWidth={1} strokeDasharray="3 2" opacity={0.7} />
                )}
              </g>
            ))}
          </svg>
          <div className="text-[10px] text-slate-500 mt-1">
            Green dots = Pareto-optimal placements ({pareto.length}). Slate dots = dominated. The user picks
            their preferred trade-off along the front.
          </div>
        </div>
      )}

      {pareto.length > 0 && (
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-2">Pareto-optimal solutions ({pareto.length})</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-800">
                <tr>
                  <th className="px-2 py-1.5 text-left text-slate-300">#</th>
                  <th className="px-2 py-1.5 text-right text-slate-300">Modules</th>
                  <th className="px-2 py-1.5 text-right text-slate-300">Airflow (Nm³/h)</th>
                  <th className="px-2 py-1.5 text-right text-slate-300">Mounting (m)</th>
                  <th className="px-2 py-1.5 text-right text-slate-300">Cost (£M)</th>
                  <th className="px-2 py-1.5 text-right text-slate-300">Anoxia (days/yr)</th>
                </tr>
              </thead>
              <tbody>
                {pareto.slice(0, 12).map((s, i) => (
                  <tr key={i} className="border-b border-slate-800">
                    <td className="px-2 py-1 text-slate-500">{i + 1}</td>
                    <td className="px-2 py-1 text-right text-slate-100">{s.v.modules}</td>
                    <td className="px-2 py-1 text-right text-slate-100">{s.v.airflow.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right text-slate-100">{s.v.mountingHeight.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right text-emerald-300 tabular-nums">£{s.f1.toFixed(3)}</td>
                    <td className="px-2 py-1 text-right text-amber-300 tabular-nums">{s.f2.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Methodology
        </div>
        <div className="text-xs text-slate-400 leading-relaxed">
          Non-dominated Sorting Genetic Algorithm II. Population evolves through tournament selection +
          crossover + mutation. Each generation merges parents + offspring, ranks by non-domination, fills
          new population from best fronts first. Crowding distance maintains diversity along the Pareto
          front. Decision variables: module count, per-diffuser airflow, mounting height. Objectives:
          minimise lifetime cost AND minimise hypolimnion anoxia. Reference: Reed et al. 2003.
        </div>
      </div>
    </div>
  );
}
